// Layouts/MediaLibrary/MediaLibrary.tsx
// Super admin Media Library page — upload once, reuse everywhere.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import MediaBrowser, { MediaBrowserHandle } from './MediaBrowser';
import { MediaItem, MediaItemDetails, KIND_STYLE, formatMediaSize, formatMediaDate } from './mediaTypes';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f1f5f5', fontSize: '0.82rem' }}>
    <span style={{ color: '#64748b', flexShrink: 0 }}>{label}</span>
    <span style={{ color: '#1a1a1a', fontWeight: 500, textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
  </div>
);

const MediaLibrary: React.FC = () => {
  const toast = useRef<Toast>(null);
  const browser = useRef<MediaBrowserHandle>(null);

  const [total, setTotal] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<MediaItemDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadDetails = useCallback(async (id: string) => {
    setDetailsLoading(true);
    try {
      const res = await apiClient.get(`/v1/media/${id}`);
      if (res.data.success) {
        setDetails(res.data.data);
        setTitleDraft(res.data.data.title);
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Could not load file details', life: 4000 });
      setOpenId(null);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (openId) { setDetails(null); loadDetails(openId); }
  }, [openId, loadDetails]);

  const handleRename = async () => {
    if (!details) return;
    const title = titleDraft.trim();
    if (!title || title === details.title) return;
    setSaving(true);
    try {
      const res = await apiClient.patch(`/v1/media/${details.id}`, { title });
      if (res.data.success) {
        setDetails((d) => (d ? { ...d, title: res.data.data.title } : d));
        browser.current?.patchItem(res.data.data as MediaItem);
        toast.current?.show({ severity: 'success', summary: 'Renamed', detail: 'Files already attached keep working.', life: 3000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Could not rename', life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!details) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/v1/media/${details.id}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'File removed from the library and storage.', life: 3000 });
      setConfirmDelete(false);
      setOpenId(null);
      browser.current?.refresh();
    } catch (err: any) {
      setConfirmDelete(false);
      toast.current?.show({ severity: 'error', summary: 'Cannot delete', detail: err.response?.data?.error || 'Could not delete the file', life: 5000 });
      if (details) loadDetails(details.id);   // usage may have changed
    } finally {
      setDeleting(false);
    }
  };

  const copyUrl = async () => {
    if (!details) return;
    try {
      await navigator.clipboard.writeText(details.url);
      toast.current?.show({ severity: 'success', summary: 'Link copied', life: 1800 });
    } catch {
      toast.current?.show({ severity: 'warn', summary: 'Copy failed', detail: details.url, life: 5000 });
    }
  };

  const kind = details ? KIND_STYLE[details.type] || KIND_STYLE.other : KIND_STYLE.other;
  const inUse = (details?.usage.total ?? 0) > 0;

  return (
    <div style={{ padding: 0, margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={confirmDelete}
        onHide={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete File"
        message={`Delete "${details?.title ?? ''}" permanently from the library and storage? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', borderRadius: '20px', padding: '32px', marginBottom: '24px', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)' }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Media Management
          </div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: 700, color: 'white', letterSpacing: '-0.5px' }}>Media Library</h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
            Upload a file once, then attach it to any study or practical material — no re-uploading
          </p>
          <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
            <i className="pi pi-images" style={{ marginRight: '6px' }} />
            {total} file{total !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <MediaBrowser ref={browser} mode="manage" onOpen={(item) => setOpenId(item.id)} activeId={openId} onTotalChange={setTotal} />

      {/* Details panel */}
      <Sidebar
        visible={!!openId}
        onHide={() => setOpenId(null)}
        position="right"
        showCloseIcon={false}
        style={{ width: '440px', maxWidth: '100vw' }}
        pt={{ content: { style: { padding: 0 } }, header: { style: { padding: 0, display: 'none' } } }}
      >
        <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', padding: '22px 24px', color: 'white', position: 'relative' }}>
          <button
            type="button" onClick={() => setOpenId(null)} aria-label="Close"
            style={{ position: 'absolute', top: '16px', right: '16px', width: '32px', height: '32px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white', cursor: 'pointer' }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.9rem' }} />
          </button>
          <div style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', opacity: 0.8, marginBottom: '6px' }}>File details</div>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, paddingRight: '40px', wordBreak: 'break-word' }}>{details?.title ?? 'Loading…'}</h2>
        </div>

        <div style={{ padding: '20px 24px', background: '#fafafa', minHeight: 'calc(100% - 90px)' }}>
          {detailsLoading || !details ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="3" /></div>
          ) : (
            <>
              {/* preview */}
              <div style={{ height: '200px', borderRadius: '14px', background: kind.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '18px', border: '1px solid #eef0f0' }}>
                {details.type === 'image'
                  ? <img src={details.url} alt={details.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <div style={{ textAlign: 'center', color: kind.color }}><i className={`pi ${kind.icon}`} style={{ fontSize: '3.4rem' }} /><div style={{ fontWeight: 700, marginTop: '8px' }}>{details.ext.toUpperCase()}</div></div>}
              </div>

              {/* rename */}
              <div style={{ background: 'white', border: '1px solid #eef0f0', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
                <GlobalInputField label="Display name" value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} maxLength={200} />
                <div style={{ marginTop: '10px' }}>
                  <GlobalButton label={saving ? 'Saving…' : 'Save name'} variant="secondary" icon="pi pi-check" onClick={handleRename}
                    disabled={saving || !titleDraft.trim() || titleDraft.trim() === details.title} fullWidth />
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '8px' }}>
                  Only the name shown here changes. The file link stays the same, so nothing already attached breaks.
                </div>
              </div>

              {/* info */}
              <div style={{ background: 'white', border: '1px solid #eef0f0', borderRadius: '14px', padding: '6px 16px', marginBottom: '16px' }}>
                <Row label="Type">{kind.label} (.{details.ext})</Row>
                <Row label="Size">{formatMediaSize(details.size)}</Row>
                <Row label="Added">{formatMediaDate(details.created_at)}</Row>
                <Row label="Source">{details.source === 'backfill' ? 'Existing upload (imported)' : 'Uploaded here'}</Row>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
                <div style={{ flex: 1 }}><GlobalButton label="Copy link" variant="outline" icon="pi pi-link" onClick={copyUrl} fullWidth /></div>
                <div style={{ flex: 1 }}><GlobalButton label="Open" variant="outline" icon="pi pi-external-link" onClick={() => window.open(details.url, '_blank', 'noopener')} fullWidth /></div>
              </div>

              {/* usage */}
              <div style={{ background: 'white', border: '1px solid #eef0f0', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '10px' }}>
                  Used in ({details.usages.length})
                </div>
                {details.usages.length === 0 ? (
                  <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Not attached to any material yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {details.usages.map((u) => (
                      <div key={`${u.type}-${u.id}`} style={{ padding: '10px 12px', background: '#f8fafa', border: '1px solid #eef2f2', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a' }}>{u.title}</span>
                          <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', background: u.type === 'study' ? '#eef2ff' : '#fff7ed', color: u.type === 'study' ? '#4338ca' : '#c2410c' }}>
                            {u.type === 'study' ? 'Study' : 'Practical'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                          {[u.course_title, u.module_title, u.chapter_title].filter(Boolean).join(' › ') || 'Whole course'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <GlobalButton label="Delete permanently" variant="danger" icon="pi pi-trash" onClick={() => setConfirmDelete(true)} disabled={inUse || deleting} fullWidth />
              {inUse && (
                <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: '8px', textAlign: 'center' }}>
                  In use — remove it from the materials above before deleting.
                </div>
              )}
            </>
          )}
        </div>
      </Sidebar>
    </div>
  );
};

export default MediaLibrary;
